/**
 * Attachment feature configuration (TUL-3848)
 * Centralized config for file attachments on prebookings
 *
 * IMPORTANT: FILE_TYPES is the single source of truth.
 * All other constants are derived from it - DO NOT add file types elsewhere.
 */

// File size limit (5MB)
export const ATTACHMENT_MAX_SIZE = 5 * 1024 * 1024;

// Attachment status constants (must match backend)
export const ATTACHMENT_STATUS = {
  PENDING: 'pending',
  CONVERTING: 'converting',
  READY: 'ready',
  CONVERSION_FAILED: 'conversion_failed',
  DELETED: 'deleted',
};

// Polling interval for conversion status (in milliseconds)
export const ATTACHMENT_POLLING_INTERVAL = 3000; // 3 seconds

/**
 * Single source of truth for all supported file types.
 * Add new file types here - all other constants are derived automatically.
 */
export const FILE_TYPES = {
  PDF: {
    extensions: ['.pdf'],
    mimeType: 'application/pdf',
    displayName: 'PDF',
    requiresConversion: false,
    signatures: ['25504446'], // %PDF
  },
  DOCX: {
    extensions: ['.docx'],
    mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    displayName: 'DOCX',
    requiresConversion: true,
    signatures: ['504b0304'], // PK.. (ZIP-based)
  },
  PPTX: {
    extensions: ['.pptx'],
    mimeType: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    displayName: 'PPTX',
    requiresConversion: true,
    signatures: ['504b0304'], // PK.. (ZIP-based)
  },
  TXT: {
    extensions: ['.txt'],
    mimeType: 'text/plain',
    displayName: 'TXT',
    requiresConversion: true,
    signatures: null, // Cannot reliably check plain text magic bytes
  },
  PNG: {
    extensions: ['.png'],
    mimeType: 'image/png',
    displayName: 'PNG',
    requiresConversion: false,
    signatures: ['89504e470d0a1a0a'], // .PNG....
  },
  JPEG: {
    extensions: ['.jpg', '.jpeg'],
    mimeType: 'image/jpeg',
    displayName: 'JPG',
    requiresConversion: false,
    signatures: ['ffd8ffe0', 'ffd8ffe1', 'ffd8ffe2', 'ffd8ffe3', 'ffd8ffe8', 'ffd8ffdb'], // JPEG SOI markers
  },
};

// ============================================================================
// TRANSLATION-SPECIFIC FILE TYPES (TUL-4760)
// Broader set of allowed types and 500MB limit for translation source files
// ============================================================================

export const TRANSLATION_MAX_SIZE = 500 * 1024 * 1024; // 500MB

export const TRANSLATION_FILE_TYPES = {
  ...FILE_TYPES,
  DOC: {
    extensions: ['.doc'],
    mimeType: 'application/msword',
    displayName: 'DOC',
    requiresConversion: false,
    signatures: null, // OLE compound document - complex magic bytes
  },
  XLS: {
    extensions: ['.xls'],
    mimeType: 'application/vnd.ms-excel',
    displayName: 'XLS',
    requiresConversion: false,
    signatures: null,
  },
  XLSX: {
    extensions: ['.xlsx'],
    mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    displayName: 'XLSX',
    requiresConversion: false,
    signatures: ['504b0304'], // ZIP-based (PK)
  },
  PPT: {
    extensions: ['.ppt'],
    mimeType: 'application/vnd.ms-powerpoint',
    displayName: 'PPT',
    requiresConversion: false,
    signatures: null,
  },
  MP3: {
    extensions: ['.mp3'],
    mimeType: 'audio/mpeg',
    displayName: 'MP3',
    requiresConversion: false,
    signatures: null, // Frame sync varies
  },
  WAV: {
    extensions: ['.wav'],
    mimeType: 'audio/wav',
    displayName: 'WAV',
    requiresConversion: false,
    signatures: null, // RIFF header varies
  },
  M4A: {
    extensions: ['.m4a'],
    mimeType: 'audio/mp4',
    displayName: 'M4A',
    requiresConversion: false,
    signatures: null, // ftyp at offset 4
  },
  OPUS: {
    extensions: ['.opus'],
    mimeType: 'audio/ogg',
    displayName: 'OPUS',
    requiresConversion: false,
    signatures: ['4f676753'], // OggS
  },
  FLAC: {
    extensions: ['.flac'],
    mimeType: 'audio/flac',
    displayName: 'FLAC',
    requiresConversion: false,
    signatures: ['664c6143'], // fLaC
  },
  MP4: {
    extensions: ['.mp4'],
    mimeType: 'video/mp4',
    displayName: 'MP4',
    requiresConversion: false,
    signatures: null, // ftyp at offset 4
  },
  AVI: {
    extensions: ['.avi'],
    mimeType: 'video/x-msvideo',
    displayName: 'AVI',
    requiresConversion: false,
    signatures: null, // RIFF header
  },
  MOV: {
    extensions: ['.mov'],
    mimeType: 'video/quicktime',
    displayName: 'MOV',
    requiresConversion: false,
    signatures: null, // ftyp at offset 4
  },
  ZIP: {
    extensions: ['.zip'],
    mimeType: 'application/zip',
    displayName: 'ZIP',
    requiresConversion: false,
    signatures: ['504b0304'], // PK
  },
  SEVEN_ZIP: {
    extensions: ['.7z'],
    mimeType: 'application/x-7z-compressed',
    displayName: '7Z',
    requiresConversion: false,
    signatures: ['377abcaf271c'], // 7z signature
  },
};

export const TRANSLATION_ALLOWED_TYPES = Object.fromEntries(
  Object.values(TRANSLATION_FILE_TYPES).map((ft) => [ft.mimeType, ft.displayName])
);

export const TRANSLATION_ALLOWED_EXTENSIONS = Object.values(TRANSLATION_FILE_TYPES).flatMap((ft) => ft.extensions);

export const TRANSLATION_EXTENSION_TO_MIME_MAP = Object.fromEntries(
  Object.values(TRANSLATION_FILE_TYPES).flatMap((ft) => ft.extensions.map((ext) => [ext, ft.mimeType]))
);

export const TRANSLATION_ACCEPT_STRING = [
  ...TRANSLATION_ALLOWED_EXTENSIONS,
  ...Object.keys(TRANSLATION_ALLOWED_TYPES),
].join(',');

export const isTranslationAllowedFileType = (mimeType) => {
  return Object.prototype.hasOwnProperty.call(TRANSLATION_ALLOWED_TYPES, mimeType);
};

export const getTranslationAllowedTypesDisplayString = () => {
  return Object.values(TRANSLATION_ALLOWED_TYPES).join(', ');
};

// ============================================================================
// DERIVED CONSTANTS - Generated from FILE_TYPES (do not edit manually)
// ============================================================================

/**
 * Content types that require PDF conversion (for display purposes)
 */
export const CONVERTIBLE_CONTENT_TYPES = Object.values(FILE_TYPES)
  .filter((ft) => ft.requiresConversion)
  .map((ft) => ft.mimeType);

/**
 * Allowed MIME types and their display names
 */
export const ATTACHMENT_ALLOWED_TYPES = Object.fromEntries(
  Object.values(FILE_TYPES).map((ft) => [ft.mimeType, ft.displayName])
);

/**
 * Allowed file extensions (secondary validation to complement MIME type check)
 */
export const ATTACHMENT_ALLOWED_EXTENSIONS = Object.values(FILE_TYPES).flatMap((ft) => ft.extensions);

/**
 * Extension to MIME type mapping
 */
export const EXTENSION_TO_MIME_MAP = Object.fromEntries(
  Object.values(FILE_TYPES).flatMap((ft) => ft.extensions.map((ext) => [ext, ft.mimeType]))
);

/**
 * File input accept attribute string
 */
export const ATTACHMENT_ACCEPT_STRING = [
  ...ATTACHMENT_ALLOWED_EXTENSIONS,
  ...Object.keys(ATTACHMENT_ALLOWED_TYPES),
].join(',');

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Check if a content type requires conversion
 * @param {string} contentType - MIME type
 * @returns {boolean}
 */
export const requiresConversion = (contentType) => {
  return CONVERTIBLE_CONTENT_TYPES.includes(contentType);
};

/**
 * Check if a file type is allowed
 * @param {string} mimeType - MIME type of the file
 * @returns {boolean}
 */
export const isAllowedFileType = (mimeType) => {
  return Object.prototype.hasOwnProperty.call(ATTACHMENT_ALLOWED_TYPES, mimeType);
};

/**
 * Get display names for allowed file types
 * @returns {string} Comma-separated list of allowed types
 */
export const getAllowedTypesDisplayString = () => {
  return Object.values(ATTACHMENT_ALLOWED_TYPES).join(', ');
};

/**
 * Get file type config by MIME type
 * @param {string} mimeType - MIME type
 * @returns {object|null} File type config or null if not found
 */
export const getFileTypeByMime = (mimeType) => {
  return Object.values(FILE_TYPES).find((ft) => ft.mimeType === mimeType) || null;
};

/**
 * Get file type config by extension
 * @param {string} extension - File extension (e.g., '.pdf')
 * @returns {object|null} File type config or null if not found
 */
export const getFileTypeByExtension = (extension) => {
  const ext = extension.toLowerCase();
  return Object.values(FILE_TYPES).find((ft) => ft.extensions.includes(ext)) || null;
};

/**
 * Validate file signature (magic bytes) against expected signatures for the MIME type
 * @param {string} signatureHex - Hex string of file's first bytes
 * @param {string} mimeType - Expected MIME type
 * @param {string} extension - File extension (used for ZIP-based formats)
 * @param {object} [fileTypes] - File types map to validate against (defaults to FILE_TYPES)
 * @returns {boolean} True if signature is valid
 */
export const isSignatureValid = (signatureHex, mimeType, extension, fileTypes = FILE_TYPES) => {
  const types = Object.values(fileTypes);
  const fileType = types.find((ft) => ft.mimeType === mimeType);

  if (!fileType) {
    // Unknown MIME type - check if it's a ZIP-based format by extension
    const extType = types.find((ft) => ft.extensions.includes(extension?.toLowerCase()));
    if (extType && extType.signatures) {
      return extType.signatures.some((sig) => signatureHex.startsWith(sig));
    }
    return false;
  }

  // No signature check for this type (e.g., plain text)
  if (fileType.signatures === null) {
    return true;
  }

  const signatureMatches = fileType.signatures.some((sig) => signatureHex.startsWith(sig));

  // For ZIP-based formats (DOCX, PPTX), also verify extension matches
  if (signatureMatches && fileType.signatures.includes('504b0304')) {
    return fileType.extensions.includes(extension.toLowerCase());
  }

  return signatureMatches;
};

export const isTranslationSignatureValid = (signatureHex, mimeType, extension) =>
  isSignatureValid(signatureHex, mimeType, extension, TRANSLATION_FILE_TYPES);
